import PropTypes from "prop-types";
import React from "react";

export const StructuredData = ({ type, data }) => {
  const generateOrganizationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Todo's Translator Dog Training",
    alternateName: "Todos Translator",
    url: "https://todostranslator.com",
    logo: "https://todostranslator.com/pawprint.svg",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-317-954-6952",
      contactType: "customer service",
      availableLanguage: "en",
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Indianapolis",
      addressRegion: "IN",
      addressCountry: "US",
    },
    sameAs: [
      "https://www.facebook.com/todostranslator",
      "https://www.instagram.com/indyinhomedogtrainer",
    ],
    description:
      "Professional dog training services and trained puppy adoption in Indianapolis. Specializing in puppy training, behavioral training, and family-ready puppies.",
  });

  const generateLocalBusinessSchema = () => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://todostranslator.com/#localbusiness",
    name: "Todo's Translator Dog Training",
    image: "https://todostranslator.com/pawprint.svg",
    telephone: "+1-317-954-6952",
    url: "https://todostranslator.com",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Indianapolis",
      addressRegion: "IN",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "39.7684",
      longitude: "-86.1581",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "20:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "10:00",
        closes: "16:00",
      },
    ],
    priceRange: "$$",
    servedCuisine: "Dog Training Services",
  });

  const generatePuppySchema = (puppy) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: puppy.cardHeaderTitle,
    description: `${puppy.breed} puppy - ${puppy.personalityTraits ? puppy.personalityTraits.join(", ") : "Professionally trained and family ready"}`,
    image:
      puppy.images && puppy.images.length > 0
        ? puppy.images[0].carouselOptimized
        : null,
    brand: {
      "@type": "Brand",
      name: "Todo's Translator Dog Training",
    },
    offers: {
      "@type": "Offer",
      availability: puppy.available
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      priceCurrency: "USD",
      price: puppy.price || "8000",
      seller: {
        "@type": "Organization",
        name: "Todo's Translator Dog Training",
      },
    },
    additionalProperty: [
      {
        "@type": "PropertyValue",
        name: "Breed",
        value: puppy.breed,
      },
      {
        "@type": "PropertyValue",
        name: "Age",
        value: puppy.age || "12-16 weeks",
      },
      {
        "@type": "PropertyValue",
        name: "Training Status",
        value: "Professionally Trained",
      },
    ],
  });

  const generateWebsiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://todostranslator.com/#website",
    url: "https://todostranslator.com",
    name: "Todo's Translator Dog Training",
    description:
      "Professional dog training services and trained puppy adoption in Indianapolis",
    publisher: {
      "@type": "Organization",
      "@id": "https://todostranslator.com/#organization",
    },
    potentialAction: [
      {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate:
            "https://todostranslator.com/puppies?search={search_term_string}",
        },
        "query-input": "required name=search_term_string",
      },
    ],
  });

  const generateServiceSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Dog Training Services",
    provider: {
      "@type": "Organization",
      name: "Todo's Translator Dog Training",
    },
    areaServed: {
      "@type": "City",
      name: "Indianapolis",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Dog Training Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Puppy Training",
            description:
              "Comprehensive puppy training including basic commands, house training, and socialization",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Behavioral Training",
            description:
              "Specialized training for behavioral issues and advanced obedience",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Trained Puppy Adoption",
            description:
              "Professionally trained puppies ready for adoption by loving families",
          },
        },
      ],
    },
  });

  const generateSchema = () => {
    switch (type) {
      case "organization":
        return generateOrganizationSchema();
      case "localbusiness":
        return generateLocalBusinessSchema();
      case "puppy":
        return generatePuppySchema(data);
      case "website":
        return generateWebsiteSchema();
      case "service":
        return generateServiceSchema();
      case "breadcrumb":
        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: data.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: item.url,
          })),
        };
      default:
        return null;
    }
  };

  const schema = generateSchema();

  if (!schema) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

StructuredData.propTypes = {
  type: PropTypes.oneOf([
    "organization",
    "localbusiness",
    "puppy",
    "website",
    "service",
    "breadcrumb",
  ]).isRequired,
  data: PropTypes.object,
};

StructuredData.defaultProps = {
  data: null,
};
